import React from 'react';
import { useGetColumnDisplayName } from 'apps/sigma2-ui/src/explore-section/hooks/useGetColumnDisplayName';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

interface GroupedBarChartPlaceholderProps {
  metricName: string;
  chartHeight: number;
}

export const GroupedBarChartPlaceholder: React.FC<
  GroupedBarChartPlaceholderProps
> = ({ metricName, chartHeight }) => {
  const { getColumnDisplayName } = useGetColumnDisplayName();
  const language = useLanguage();

  return (
    <div key={metricName} className="w-full">
      {/* Metric title */}
      <h3 className="text-base font-semibold text-gray-800 mb-3 px-2">
        {getColumnDisplayName(metricName)}
      </h3>
      <div className="w-full bg-white" style={{ height: `${chartHeight}px` }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="568"
          height="230"
          role="application"
          focusable="false"
        >
          <rect width="568" height="230" fill="transparent"></rect>
          <g transform="translate(150,10)">
            <g>
              <line
                opacity="1"
                x1="0"
                x2="368"
                y1="149"
                y2="149"
                stroke="#dddddd"
                strokeWidth="1"
              ></line>
              <line
                opacity="1"
                x1="0"
                x2="368"
                y1="117"
                y2="117"
                stroke="#dddddd"
                strokeWidth="1"
              />
              <line
                opacity="1"
                x1="0"
                x2="368"
                y1="85"
                y2="85"
                stroke="#dddddd"
                strokeWidth="1"
              />
              <line
                opacity="1"
                x1="0"
                x2="368"
                y1="53"
                y2="53"
                stroke="#dddddd"
                strokeWidth="1"
              />
              <line
                opacity="1"
                x1="0"
                x2="368"
                y1="21"
                y2="21"
                stroke="#dddddd"
                strokeWidth="1"
              />
            </g>
            <g transform="translate(0,170)">
              <line
                x1="0"
                x2="368"
                y1="0"
                y2="0"
                style={{ stroke: 'transparent', strokeWidth: '1' }}
              ></line>
              <text
                transform="translate(184, 43) rotate(0)"
                textAnchor="middle"
                style={{
                  dominantBaseline: 'central',
                  fontFamily: 'Karla, sans-serif',
                  fontSize: '12px',
                  fill: 'rgb(51, 51, 51)',
                  outlineWidth: '0px',
                  outlineColor: 'transparent',
                  fontWeight: '700',
                }}
              ></text>
            </g>
            <g transform="translate(0,0)">
              <g transform="translate(0,149)" style={{ opacity: 1 }}>
                <line
                  x1="0"
                  x2="0"
                  y1="0"
                  y2="0"
                  style={{ stroke: 'rgb(119, 119, 119)', strokeWidth: '1' }}
                />
                <text
                  dominantBaseline="central"
                  textAnchor="end"
                  transform="translate(-10,0) rotate(0)"
                  style={{
                    fontFamily: 'Karla, sans-serif',
                    fontSize: '12px',
                    fill: 'rgb(105, 109, 112)',
                  }}
                >
                  1
                </text>
              </g>
              <g transform="translate(0,117)" style={{ opacity: 1 }}>
                <line
                  x1="0"
                  x2="0"
                  y1="0"
                  y2="0"
                  style={{ stroke: 'rgb(119, 119, 119)', strokeWidth: '1' }}
                />
                <text
                  dominantBaseline="central"
                  textAnchor="end"
                  transform="translate(-10,0) rotate(0)"
                  style={{
                    fontFamily: 'Karla, sans-serif',
                    fontSize: '12px',
                    fill: 'rgb(105, 109, 112)',
                  }}
                >
                  2
                </text>
              </g>
              <g transform="translate(0,85)" style={{ opacity: 1 }}>
                <line
                  x1="0"
                  x2="0"
                  y1="0"
                  y2="0"
                  style={{ stroke: 'rgb(119, 119, 119)', strokeWidth: '1' }}
                />
                <text
                  dominantBaseline="central"
                  textAnchor="end"
                  transform="translate(-10,0) rotate(0)"
                  style={{
                    fontFamily: 'Karla, sans-serif',
                    fontSize: '12px',
                    fill: 'rgb(105, 109, 112)',
                  }}
                >
                  3
                </text>
              </g>
              <g transform="translate(0,53)" style={{ opacity: 1 }}>
                <line
                  x1="0"
                  x2="0"
                  y1="0"
                  y2="0"
                  style={{ stroke: 'rgb(119, 119, 119)', strokeWidth: '1' }}
                />
                <text
                  dominantBaseline="central"
                  textAnchor="end"
                  transform="translate(-10,0) rotate(0)"
                  style={{
                    fontFamily: 'Karla, sans-serif',
                    fontSize: '12px',
                    fill: 'rgb(105, 109, 112)',
                  }}
                >
                  4
                </text>
              </g>
              <g transform="translate(0,21)" style={{ opacity: 1 }}>
                <line
                  x1="0"
                  x2="0"
                  y1="0"
                  y2="0"
                  style={{ stroke: 'rgb(119, 119, 119)', strokeWidth: '1' }}
                />
                <text
                  dominantBaseline="central"
                  textAnchor="end"
                  transform="translate(-10,0) rotate(0)"
                  style={{
                    fontFamily: 'Karla, sans-serif',
                    fontSize: '12px',
                    fill: 'rgb(105, 109, 112)',
                  }}
                >
                  5
                </text>
              </g>
              <line
                x1="0"
                x2="0"
                y1="0"
                y2="170"
                style={{ stroke: 'transparent', strokeWidth: '1' }}
              />
              <text
                transform="translate(-60, 85) rotate(-90)"
                textAnchor="middle"
                style={{
                  dominantBaseline: 'central',
                  fontFamily: 'Karla, sans-serif',
                  fontSize: '12px',
                  fill: 'rgb(51, 51, 51)',
                  outlineWidth: '0px',
                  outlineColor: 'transparent',
                  fontWeight: '700',
                }}
              ></text>
            </g>
            <g width="fit-content">
              <g transform="translate(0, 138)">
                <rect width="189" height="22px" fill="transparent"></rect>
              </g>
              <g transform="translate(0, 138)">
                <defs>
                  <clipPath id="bb2106ed-f1ce-4e58-bae1-100e48cfc156">
                    <rect
                      x="-4"
                      y="0"
                      rx="4"
                      ry="4"
                      width="193"
                      height="22"
                    ></rect>
                  </clipPath>
                </defs>
                <g clipPath="url(#bb2106ed-f1ce-4e58-bae1-100e48cfc156)">
                  <g transform="">
                    <rect
                      width="189"
                      height="22"
                      rx="0"
                      ry="0"
                      fill="#6b7280"
                      strokeWidth="1"
                      stroke="rgba(0, 0, 0, 0)"
                    ></rect>
                  </g>
                </g>
              </g>
            </g>
            <g width="fit-content">
              <g transform="translate(0, 106)">
                <rect width="211" height="22px" fill="transparent"></rect>
              </g>
              <g transform="translate(0, 106)">
                <defs>
                  <clipPath id="7325bbd7-7824-45b2-92cb-dac31346981f">
                    <rect
                      x="-4"
                      y="0"
                      rx="4"
                      ry="4"
                      width="215"
                      height="22"
                    ></rect>
                  </clipPath>
                </defs>
                <g clipPath="url(#7325bbd7-7824-45b2-92cb-dac31346981f)">
                  <g transform="">
                    <rect
                      width="211"
                      height="22"
                      rx="0"
                      ry="0"
                      fill="#9ca3af"
                      strokeWidth="1"
                      stroke="rgba(0, 0, 0, 0)"
                    ></rect>
                  </g>
                </g>
              </g>
            </g>
            <g width="fit-content">
              <g transform="translate(0, 74)">
                <rect width="232" height="22px" fill="transparent"></rect>
              </g>
              <g transform="translate(0, 74)">
                <defs>
                  <clipPath id="82202c18-4407-447a-b383-1668699b3b2b">
                    <rect
                      x="-4"
                      y="0"
                      rx="4"
                      ry="4"
                      width="236"
                      height="22"
                    ></rect>
                  </clipPath>
                </defs>
                <g clipPath="url(#82202c18-4407-447a-b383-1668699b3b2b)">
                  <g transform="">
                    <rect
                      width="232"
                      height="22"
                      rx="0"
                      ry="0"
                      fill="#d1d5db"
                      strokeWidth="1"
                      stroke="rgba(0, 0, 0, 0)"
                    ></rect>
                  </g>
                </g>
              </g>
            </g>
            <g width="fit-content">
              <g transform="translate(0, 42)">
                <rect width="264" height="22px" fill="transparent"></rect>
              </g>
              <g transform="translate(0, 42)">
                <defs>
                  <clipPath id="2d9fff00-4067-4419-a418-45e9c2a43fee">
                    <rect
                      x="-4"
                      y="0"
                      rx="4"
                      ry="4"
                      width="268"
                      height="22"
                    ></rect>
                  </clipPath>
                </defs>
                <g clipPath="url(#2d9fff00-4067-4419-a418-45e9c2a43fee)">
                  <g transform="">
                    <rect
                      width="264"
                      height="22"
                      rx="0"
                      ry="0"
                      fill="#e5e7eb"
                      strokeWidth="1"
                      stroke="rgba(0, 0, 0, 0)"
                    ></rect>
                  </g>
                </g>
              </g>
            </g>
            <g width="fit-content">
              <g transform="translate(0, 10)">
                <rect width="279" height="22px" fill="transparent"></rect>
              </g>
              <g transform="translate(0, 10)">
                <defs>
                  <clipPath id="d47bddc7-ce60-40a2-bc69-8d3aa50f51be">
                    <rect
                      x="-4"
                      y="0"
                      rx="4"
                      ry="4"
                      width="283"
                      height="22"
                    ></rect>
                  </clipPath>
                </defs>
                <g clipPath="url(#d47bddc7-ce60-40a2-bc69-8d3aa50f51be)">
                  <g transform="">
                    <rect
                      width="279"
                      height="22"
                      rx="0"
                      ry="0"
                      fill="#f3f4f6"
                      strokeWidth="1"
                      stroke="rgba(0, 0, 0, 0)"
                    ></rect>
                  </g>
                </g>
              </g>
            </g>
            <g transform="translate(0, 0)">
              <line
                x1="0"
                x2="0"
                y1="0"
                y2="170"
                stroke="#000000"
                strokeWidth="1"
                style={{ stroke: 'rgb(184, 186, 188)', strokeWidth: '1px' }}
              />
            </g>
            <line
              x1="0"
              y1="170"
              x2="368"
              y2="170"
              stroke="#b8babc"
              strokeWidth="1"
            />
          </g>
          <text
            transform="translate(150,200) rotate(0)"
            style={{
              fontFamily: 'Karla, sans-serif',
              fontSize: '13px',
              fill: 'rgb(51, 51, 51)',
            }}
          >
            {language.cannotDisplayCountsForMetric()}
          </text>
        </svg>
      </div>
    </div>
  );
};
